import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import Login from "./Login";
import api from "../api";
import "../App.css";

const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i;
const PLATE_REGEX = /^[A-Z0-9]{1,10}$/i;

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],
  ["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],
  ["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],
  ["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],["KS","Kansas"],
  ["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],
  ["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],
  ["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],
  ["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],["OK","Oklahoma"],
  ["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],
  ["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],
  ["WI","Wisconsin"],["WY","Wyoming"],["DC","Washington D.C."],
];

/* ── Navbar brand icon (grid from Logo 2) ── */
const BrandIcon = () => (
  <svg width="28" height="36" viewBox="0 0 50 62" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="5"  cy="8"  r="3.8" fill="#00b8b8"/>
    <circle cx="5"  cy="22" r="3.8" fill="#00b8b8"/>
    <circle cx="5"  cy="36" r="3.8" fill="#00b8b8"/>
    <circle cx="5"  cy="50" r="3.8" fill="#00b8b8"/>
    <rect x="13" y="4"  width="8" height="8" rx="1.5" fill="#00b8b8"/>
    <rect x="13" y="18" width="8" height="8" rx="1.5" fill="#00b8b8"/>
    <rect x="13" y="32" width="8" height="8" rx="1.5" fill="#00b8b8"/>
    <rect x="13" y="46" width="8" height="8" rx="1.5" fill="#00b8b8"/>
    <rect x="25" y="2"  width="11" height="11" rx="2" fill="#00b8b8"/>
    <rect x="25" y="17" width="11" height="11" rx="2" fill="#00b8b8"/>
    <rect x="25" y="32" width="11" height="11" rx="2" fill="#00b8b8"/>
    <rect x="40" y="0"  width="14" height="14" rx="2.5" fill="#00b8b8"/>
    <rect x="40" y="18" width="14" height="14" rx="2.5" fill="#00b8b8"/>
  </svg>
);


const CarIllustration = () => (
  <svg role="img" aria-label="Illustration of a vehicle — representing the cars our reports cover"
       viewBox="0 0 420 220" style={{ width: "100%", maxWidth: "500px" }} xmlns="http://www.w3.org/2000/svg">
    {/* Ground shadow */}
    <ellipse cx="210" cy="208" rx="188" ry="12" fill="rgba(26,115,232,0.1)" />

    {/* Car body */}
    <path d="M28,180 L28,148 L58,136 L94,96 L296,96 L342,136 L392,148 L392,180 Z" fill="#1565c0" />

    {/* Cabin/roof */}
    <path d="M96,96 L120,68 L288,68 L314,96 Z" fill="#1976d2" />

    {/* Front window */}
    <path d="M99,96 L123,69 L170,69 L170,96 Z" fill="#bbdefb" opacity="0.92" />
    {/* Middle window */}
    <rect x="173" y="69" width="58" height="27" fill="#bbdefb" opacity="0.92" />
    {/* Rear window */}
    <path d="M234,96 L234,69 L285,69 L311,96 Z" fill="#bbdefb" opacity="0.92" />

    {/* Window pillars */}
    <line x1="172" y1="96" x2="172" y2="136" stroke="rgba(255,255,255,0.28)" strokeWidth="2" />
    <line x1="233" y1="96" x2="233" y2="136" stroke="rgba(255,255,255,0.28)" strokeWidth="2" />

    {/* Front lower panel */}
    <path d="M28,148 L58,136 L94,136 L94,180 L28,180 Z" fill="#0d47a1" />
    {/* Rear lower panel */}
    <path d="M314,136 L354,136 L392,148 L392,180 L314,180 Z" fill="#0d47a1" />

    {/* Headlight */}
    <rect x="33" y="140" width="30" height="15" rx="5" fill="#fff9c4" />
    <rect x="33" y="140" width="30" height="15" rx="5" fill="none" stroke="#f9a825" strokeWidth="1" />

    {/* Tail light */}
    <rect x="351" y="140" width="26" height="15" rx="5" fill="#ffcdd2" />
    <rect x="351" y="140" width="26" height="15" rx="5" fill="none" stroke="#c62828" strokeWidth="1" />

    {/* Body accent line */}
    <line x1="28" y1="162" x2="392" y2="162" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />

    {/* Front wheel arch */}
    <path d="M58,180 Q58,144 105,144 Q152,144 152,180" fill="#0a3d91" />
    {/* Rear wheel arch */}
    <path d="M268,180 Q268,144 315,144 Q362,144 362,180" fill="#0a3d91" />

    {/* Front wheel */}
    <circle cx="105" cy="186" r="28" fill="#1a1a1a" />
    <circle cx="105" cy="186" r="19" fill="#2e2e2e" />
    <circle cx="105" cy="186" r="7" fill="#888" />
    <line x1="105" y1="167" x2="105" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="105" y1="186" x2="105" y2="205" stroke="#555" strokeWidth="3.5" />
    <line x1="86" y1="186" x2="105" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="105" y1="186" x2="124" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="92" y1="173" x2="105" y2="186" stroke="#555" strokeWidth="2.5" />
    <line x1="118" y1="173" x2="105" y2="186" stroke="#555" strokeWidth="2.5" />

    {/* Rear wheel */}
    <circle cx="315" cy="186" r="28" fill="#1a1a1a" />
    <circle cx="315" cy="186" r="19" fill="#2e2e2e" />
    <circle cx="315" cy="186" r="7" fill="#888" />
    <line x1="315" y1="167" x2="315" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="315" y1="186" x2="315" y2="205" stroke="#555" strokeWidth="3.5" />
    <line x1="296" y1="186" x2="315" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="315" y1="186" x2="334" y2="186" stroke="#555" strokeWidth="3.5" />
    <line x1="302" y1="173" x2="315" y2="186" stroke="#555" strokeWidth="2.5" />
    <line x1="328" y1="173" x2="315" y2="186" stroke="#555" strokeWidth="2.5" />
  </svg>
);

export default function Home() {
  const [inputMode, setInputMode] = useState("vin");   // "vin" | "plate"
  const [vin, setVin] = useState("");
  const [plate, setPlate] = useState("");
  const [plateState, setPlateState] = useState("");
  const [email, setEmail] = useState("");
  const [vinError, setVinError] = useState("");
  const [plateError, setPlateError] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = "Vehicle History Report | Vehicle History Now";
    try {
      const stored = localStorage.getItem("user");
      if (stored) setUser(JSON.parse(stored));
    } catch {
      // ignore malformed data
    }
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("user");
    // Clear HttpOnly JWT cookies via backend
    api.post("/api/auth/logout/").catch(() => {});
    setUser(null);
  };

  const handleCheckout = async () => {
    if (loading) return;

    // ── Validate inputs based on mode ───────────────────────────
    if (inputMode === "vin") {
      const cleanVin = vin.trim().toUpperCase();
      if (!cleanVin || !VIN_REGEX.test(cleanVin)) {
        setVinError("Please enter a valid 17-character VIN (letters A–Z except I, O, Q, and digits 0–9).");
        return;
      }
      setVinError("");
    } else {
      let err = "";
      if (!plateState) err = "Please select a state.";
      else if (!plate.trim() || !PLATE_REGEX.test(plate.trim())) err = "Please enter a valid license plate (1–10 letters/digits).";
      if (err) { setPlateError(err); return; }
      setPlateError("");
    }

    setLoading(true);

    try {
      const payload = inputMode === "vin"
        ? { vin: vin.trim().toUpperCase(), email: email.trim() || undefined }
        : { state: plateState, plate_no: plate.trim().toUpperCase(), email: email.trim() || undefined };

      const res = await api.post("/api/checkout/", payload);

      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }

      if (res.data?.sessionId) {
        const stripe = await stripePromise;
        if (!stripe) {
          alert("Payment system failed to load. Please refresh and try again.");
          return;
        }
        const result = await stripe.redirectToCheckout({ sessionId: res.data.sessionId });
        if (result.error) alert(result.error.message);
        return;
      }

      alert("Unexpected response from server. Please try again.");
    } catch (err) {
      console.error("Checkout error:", err);
      if (err.response) {
        alert(`Error: ${err.response.data?.error || err.response.statusText}`);
      } else if (err.request) {
        alert("Could not reach the server. Please check your connection and try again.");
      } else {
        alert("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ── NAVBAR ── */}
      <nav className="navbar">
        <div className="navbar-brand">
          <BrandIcon />
          <span>Vehicle History Now</span>
        </div>
        {user ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name || "Profile"}
                width="32"
                height="32"
                referrerPolicy="no-referrer"
                style={{ borderRadius: "50%", border: "2px solid #e8eaed" }}
              />
            )}
            <span style={{ fontSize: "14px", fontWeight: 600, color: "#0d2137" }}>
              {user.name || user.email}
            </span>
            <button
              onClick={handleSignOut}
              style={{
                background: "#f1f3f4",
                border: "1px solid #dadce0",
                color: "#444",
                padding: "5px 14px",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Sign out
            </button>
          </div>
        ) : (
          <span className="navbar-tagline">
            Trusted vehicle data &nbsp;·&nbsp;{" "}
            <del style={{ opacity: 0.55 }}>$5</del> <strong style={{ color: "#1a73e8" }}>$3</strong>
          </span>
        )}
      </nav>

      {/* ── MAIN CONTENT ── */}
      <main id="main-content">

      {/* ── HERO ── */}
      <section className="hero" aria-label="Vehicle history report lookup">
        <div className="hero-left">
          <span className="hero-badge">⚡ Instant Results — No Subscription</span>
          <h1>Know Everything About Any Vehicle</h1>
          <p className="hero-sub">
            Full accident history, title check, mileage records, and more — in seconds.
          </p>

          {/* ── PRICE COMPARISON ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "0 0 22px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Regular price:</span>
            <del style={{ fontSize: "22px", color: "#9ca3af", fontWeight: 600 }}>$5.00</del>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>→</span>
            <strong style={{ fontSize: "32px", color: "#1a73e8", fontWeight: 900, lineHeight: 1 }}>$3.00</strong>
            <span style={{
              background: "#dc2626",
              color: "#fff",
              borderRadius: "6px",
              padding: "4px 10px",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.4px",
            }}>40% OFF</span>
          </div>

          <div className="vin-form">
            {/* ── Tab toggle ── */}
            <div style={{ display: "flex", marginBottom: "14px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e4eaf4" }}>
              {[["vin", "🔢 VIN Number"], ["plate", "🚘 License Plate"]].map(([mode, label]) => (
                <button
                  key={mode}
                  onClick={() => { setInputMode(mode); setVinError(""); setPlateError(""); }}
                  disabled={loading}
                  style={{
                    flex: 1, padding: "9px 0", border: "none", cursor: "pointer",
                    fontSize: "13px", fontWeight: 600, transition: "background 0.15s",
                    background: inputMode === mode ? "#1a73e8" : "#f8f9ff",
                    color: inputMode === mode ? "#fff" : "#555",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── VIN input ── */}
            {inputMode === "vin" && (
              <>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => { setVin(e.target.value); if (vinError) setVinError(""); }}
                  placeholder="Enter 17-character VIN number"
                  maxLength={17}
                  disabled={loading}
                />
                {vinError && <p className="vin-error">{vinError}</p>}
              </>
            )}

            {/* ── License plate inputs ── */}
            {inputMode === "plate" && (
              <>
                <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                  <select
                    value={plateState}
                    onChange={(e) => { setPlateState(e.target.value); if (plateError) setPlateError(""); }}
                    disabled={loading}
                    style={{
                      width: "42%", padding: "13px 10px", borderRadius: "8px",
                      border: "1px solid #dde3ef", fontSize: "14px", color: plateState ? "#222" : "#999",
                      background: "#fff",
                    }}
                  >
                    <option value="">State</option>
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code}>{code} — {name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={plate}
                    onChange={(e) => { setPlate(e.target.value); if (plateError) setPlateError(""); }}
                    placeholder="License plate"
                    maxLength={10}
                    disabled={loading}
                    style={{ flex: 1 }}
                  />
                </div>
                {plateError && <p className="vin-error">{plateError}</p>}
              </>
            )}

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address (optional — get a copy)"
              disabled={loading}
            />
            <button onClick={handleCheckout} disabled={loading}>
              {loading ? "Redirecting to payment…" : "Get My Vehicle Report — $3"}
            </button>
            <p className="price-note">
              🔒 Secure payment via Stripe &nbsp;·&nbsp; No subscription &nbsp;·&nbsp; One-time fee
            </p>
          </div>
        </div>

        <div className="hero-right">
          <div className="stat-pills">
            <span className="stat-pill">🚗 500K+ Reports Run</span>
            <span className="stat-pill">⭐ Comprehensive Data</span>
          </div>
          <CarIllustration />
          <div className="stat-pills">
            <span className="stat-pill">🔒 PCI Secure Checkout</span>
            <span className="stat-pill">📧 Instant PDF Delivery</span>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <div className="trust-strip">
        {[
          { num: "500K+", label: "Reports Generated" },
          { num: "$3", label: "One-Time Price", was: "$5" },
          { num: "Instant", label: "PDF Delivery" },
          { num: "100%", label: "Secure Payment" },
        ].map((t) => (
          <div key={t.label} className="trust-item">
            <span className="trust-number">
              {t.was && (
                <del style={{ fontSize: "14px", opacity: 0.45, marginRight: "5px", fontWeight: 500 }}>
                  {t.was}
                </del>
              )}
              {t.num}
            </span>
            <span className="trust-label">{t.label}</span>
          </div>
        ))}
      </div>

      {/* ── REPORT FEATURES ── */}
      <section className="features-section">
        <h2>What's Included in Your Report</h2>
        <p className="section-sub">Everything you need to make a confident vehicle decision</p>
        <div className="features-grid">
          {[
            { icon: "💥", title: "Accident History", desc: "Reported accidents, damage severity, and collision records from insurance and DMV sources." },
            { icon: "📋", title: "Title Check", desc: "Salvage, flood, lemon law buyback, and rebuilt title flags that affect vehicle value." },
            { icon: "🔢", title: "Odometer Records", desc: "Full mileage history to detect rollback fraud before you buy." },
            { icon: "👤", title: "Ownership History", desc: "Number of previous owners, usage type (personal, fleet, rental, taxi)." },
            { icon: "🔧", title: "Service Records", desc: "Maintenance and repair history from dealerships and service centers." },
            { icon: "⚠️", title: "Open Recalls", desc: "Active safety recalls from the NHTSA database that may still need fixing." },
          ].map((f) => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── LEFT / RIGHT BANNERS ── */}
      <div className="banners-section">
        <div className="banner-left">
          <h2>Your Report Covers</h2>
          <ul className="banner-check-list">
            {[
              "Full accident and damage history",
              "Title problems: salvage, flood, lemon law",
              "Odometer rollback detection",
              "Theft and recovery records",
              "NHTSA open recall alerts",
              "Auction and fleet usage history",
              "Structural damage indicators",
              "Previous state registrations",
            ].map((item) => (
              <li key={item}>
                <span className="check-icon">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="banner-right">
          <h2>Why Vehicle History Now?</h2>
          <ul className="banner-check-list">
            {[
              { title: "Just $3 per report (was $5)", desc: "No subscriptions, no hidden fees. Pay once, get your full report instantly." },
              { title: "Instant delivery", desc: "Your PDF report is ready in seconds, sent to your email if provided." },
              { title: "Comprehensive data", desc: "Sourced from DMV, NHTSA, insurance, auction, and service records." },
              { title: "Secure checkout", desc: "Payments processed by Stripe — PCI DSS compliant and fully encrypted." },
              { title: "Google Sign-In", desc: "Sign in to access all your past reports from your personal account." },
            ].map((item) => (
              <li key={item.title}>
                <span className="check-icon">✓</span>
                <div>
                  <strong style={{ color: "#0d2137", display: "block", marginBottom: "3px" }}>
                    {item.title}
                  </strong>
                  {item.desc}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section className="how-section">
        <h2>How It Works</h2>
        <div className="steps">
          {[
            {
              n: "1",
              title: "Enter Your VIN",
              desc: "Find the 17-character VIN on the dashboard, door jamb, or vehicle registration document.",
            },
            {
              n: "2",
              title: "Pay Securely — $3",
              desc: "One-time payment via Stripe. No account needed, no recurring charges.",
            },
            {
              n: "3",
              title: "Get Your Report",
              desc: "Instant PDF with the full vehicle history, shown in your browser and emailed to you.",
            },
          ].map((s) => (
            <div key={s.n} className="step">
              <div className="step-num">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SIGN IN (hidden when already logged in) ── */}
      {!user && (
        <div className="login-section">
          <Login />
        </div>
      )}

      </main>{/* end #main-content */}

      {/* ── FOOTER ── */}
      <footer className="site-footer" aria-label="Site footer">
        <p>
          &copy; 2026 Vehicle History Now &nbsp;|&nbsp;{" "}
          <a href="/privacy-policy.html">Privacy Policy</a>
          &nbsp;|&nbsp;{" "}
          <a href="mailto:support@vehiclehistorynow.com">Contact Us</a>
        </p>
        <p style={{ margin: "4px 0 0", fontSize: "12px", opacity: 0.7 }}>
          Questions? Email us at{" "}
          <a href="mailto:support@vehiclehistorynow.com">support@vehiclehistorynow.com</a>
        </p>
      </footer>
    </div>
  );
}
